/**
 * The mention node — the old web's `CustomMention.ts` on TipTap's `Mention`.
 *
 * On top of the stock node it carries the Revit identity (`type`, `id`), the
 * family / type halves, the derivedFromType pair, and `selectedParams`, all
 * round-tripped through `data-*` attributes so that copy, paste and the draft
 * restore (`buildMentionClipboardHtml` → `parseHTML`) rebuild real chips.
 * `parseHTML` keys off the structural class `.revit-mention`: the stock
 * matcher wants `span[data-type="mention"]`, and here `data-type` is the Revit
 * element type.
 *
 * `renderHTML` is the static serialisation (clipboard, HTML mirrors); the live
 * editor shows `MentionChipNode`. Both draw from `mentionChipPresentation`.
 */
import { mergeAttributes } from "@tiptap/core";
import Mention from "@tiptap/extension-mention";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { MentionChipNode } from "../components/chat/MentionChipNode";
import {
  MENTION_WRAPPER_CLASS,
  MINOR_CHIP_CLASS,
  MINOR_CHIP_OVERFLOW_CLASS,
  getMajorChipClass,
  getMajorChipText,
  getMajorChipTooltip,
  getMinorChipTooltip,
  getOverflowTooltip,
  splitParamsForDisplay,
} from "../lib/mentionChipPresentation";
import { isSkillMentionType, type MentionParameter } from "../lib/mentionUtils";

const SCOPES = new Set(["instance", "type", "family", "layer", "block"]);

/** `data-params` back to a list; malformed JSON drops to `[]` rather than throwing in the parser. */
export function safeParseParams(raw: string | null): MentionParameter[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((p) => {
      if (!p || typeof p !== "object") return [];
      const obj = p as Record<string, unknown>;
      const id = String(obj["id"] ?? "");
      if (!id) return [];
      const result: MentionParameter = { id, name: String(obj["name"] ?? "") };
      if (typeof obj["scope"] === "string" && SCOPES.has(obj["scope"])) result.scope = obj["scope"] as MentionParameter["scope"];
      return [result];
    });
  } catch {
    return [];
  }
}

function dataAttribute(name: string, attribute: string) {
  return {
    default: null,
    parseHTML: (element: HTMLElement) => element.getAttribute(attribute),
    renderHTML: (attributes: Record<string, unknown>) => (attributes[name] ? { [attribute]: String(attributes[name]) } : {}),
  };
}

export const CustomMention = Mention.extend({
  name: "mention",

  parseHTML() {
    return [{ tag: "span.revit-mention" }];
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      type: dataAttribute("type", "data-type"),
      id: dataAttribute("id", "data-id"),
      label: dataAttribute("label", "data-label"),
      familyName: dataAttribute("familyName", "data-family-name"),
      typeName: dataAttribute("typeName", "data-type-name"),
      derivedFromTypeId: dataAttribute("derivedFromTypeId", "data-derived-type-id"),
      derivedFromTypeName: dataAttribute("derivedFromTypeName", "data-derived-type-name"),
      selectedParams: {
        default: [] as MentionParameter[],
        parseHTML: (element: HTMLElement) => safeParseParams(element.getAttribute("data-params")),
        renderHTML: (attributes: Record<string, unknown>) => {
          const params = attributes["selectedParams"];
          return Array.isArray(params) && params.length > 0 ? { "data-params": JSON.stringify(params) } : {};
        },
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(MentionChipNode);
  },

  renderHTML({ node, HTMLAttributes }) {
    const params: MentionParameter[] = Array.isArray(node.attrs["selectedParams"]) ? node.attrs["selectedParams"] : [];
    const type = String(node.attrs["type"] ?? "");
    const id = String(node.attrs["id"] ?? "");
    const label = String(node.attrs["label"] ?? "");

    const base: Record<string, string | null> = {
      class: MENTION_WRAPPER_CLASS,
      "data-type": type || null,
      "data-id": id || null,
      "data-label": label || null,
    };
    if (node.attrs["familyName"]) base["data-family-name"] = String(node.attrs["familyName"]);
    if (node.attrs["typeName"]) base["data-type-name"] = String(node.attrs["typeName"]);
    if (node.attrs["derivedFromTypeId"]) base["data-derived-type-id"] = String(node.attrs["derivedFromTypeId"]);
    if (node.attrs["derivedFromTypeName"]) base["data-derived-type-name"] = String(node.attrs["derivedFromTypeName"]);
    if (params.length > 0) base["data-params"] = JSON.stringify(params);

    // No React context here, so no cache to ask: the optimistic blue, which
    // only the clipboard / HTML mirror ever sees.
    const major = [
      "span",
      { class: getMajorChipClass(isSkillMentionType(type) ? false : undefined), title: getMajorChipTooltip(type, id) },
      getMajorChipText(label),
    ] as const;
    const { visible, overflow } = splitParamsForDisplay(params);
    const minors = visible.map((p) => {
      const attrs: Record<string, string> = { class: MINOR_CHIP_CLASS, title: getMinorChipTooltip(p), "data-param-id": String(p.id) };
      if (p.scope) attrs["data-param-scope"] = p.scope;
      return ["span", attrs, p.name] as const;
    });
    const tail = overflow.length > 0 ? [["span", { class: MINOR_CHIP_OVERFLOW_CLASS, title: getOverflowTooltip(overflow) }, `+${overflow.length}`] as const] : [];

    return ["span", mergeAttributes(base, this.options.HTMLAttributes, HTMLAttributes), major, ...minors, ...tail];
  },
});
